import React, { useState, useEffect } from 'react';

import { X } from 'lucide-react';
import { toast } from 'sonner';

import AnswerEditorSimple from './components/AnswerEditorSimple';
import AnswerPreview from './components/AnswerPreview';
import CitationAssigner from './components/CitationAssigner';
import CitationList from './components/CitationList';
import ModalFooter from './components/ModalFooter';
import QuestionInput from './components/QuestionInput';
import { useCreateManualThread } from '@/api/mutations/useCreateManualThread';
import type { ReferencesDict } from '@/api/mutations/useCreateManualThread';
import { useGetChunk } from '@/api/queries/useGetChunk';
import { useGetFact } from '@/api/queries/useGetFact';

interface ManualAnswerModalProps {
  caseId: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (threadId: string) => void;
}

const ManualAnswerModal: React.FC<ManualAnswerModalProps> = ({ caseId, isOpen, onClose, onComplete }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [references, setReferences] = useState<ReferencesDict>({});
  const [selectedCitation, setSelectedCitation] = useState<string | null>(null);
  const [citationNumbers, setCitationNumbers] = useState<string[]>([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const { mutate: createThread, isLoading: isCreating } = useCreateManualThread();

  // Get the current reference details
  const currentRef = selectedCitation ? references[selectedCitation] : null;
  const { data: currentFact } = useGetFact(caseId, currentRef?.type === 'fact' ? currentRef.id : null);
  const { data: currentChunk } = useGetChunk(caseId, currentRef?.type === 'chunk' ? currentRef.id : null);

  // Extract citation numbers from answer
  useEffect(() => {
    const regex = /\[(\d+)\]/g;
    const numbers = new Set<string>();
    let match;
    while ((match = regex.exec(answer)) !== null) {
      numbers.add(match[1]);
    }
    const sorted = Array.from(numbers).sort((a, b) => parseInt(a) - parseInt(b));
    setCitationNumbers(sorted);

    // Auto-select first unassigned citation
    if (selectedCitation === null || !sorted.includes(selectedCitation)) {
      const firstUnassigned = sorted.find((n) => !references[n]);
      setSelectedCitation(firstUnassigned || (sorted.length > 0 ? sorted[0] : null));
    }
  }, [answer, references, selectedCitation]);

  const handleAssignReference = (id: string, type: 'fact' | 'chunk') => {
    if (!selectedCitation) return;

    const updatedRefs = {
      ...references,
      [selectedCitation]: { id, type },
    };
    setReferences(updatedRefs);
  };

  const handleRemoveReference = (citationNum: string) => {
    const updatedRefs = { ...references };
    delete updatedRefs[citationNum];
    setReferences(updatedRefs);
  };

  const handleSave = () => {
    if (!question.trim()) {
      toast.error('Please enter a question');
      return;
    }
    if (!answer.trim()) {
      toast.error('Please enter an answer');
      return;
    }

    // Check for unassigned citations
    const assignedCount = Object.keys(references).length;
    const totalCount = citationNumbers.length;
    if (assignedCount < totalCount) {
      toast.error('All citations must be assigned to a fact or chunk before saving');
      return;
    }

    // Create thread
    createThread(
      {
        caseId,
        question,
        answer,
        references,
      },
      {
        onSuccess: (data) => {
          toast.success('Manual answer saved successfully');
          // Clear all state
          setQuestion('');
          setAnswer('');
          setReferences({});
          setSelectedCitation(null);
          setCitationNumbers([]);
          setIsPreviewMode(false);
          onComplete(data.threadId);
          onClose();
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to save manual answer');
        },
      },
    );
  };

  const handleClose = () => {
    if (question.trim() || answer.trim()) {
      if (confirm('You have unsaved changes. Are you sure you want to close?')) {
        // Clear all state
        setQuestion('');
        setAnswer('');
        setReferences({});
        setSelectedCitation(null);
        setCitationNumbers([]);
        setIsPreviewMode(false);
        onClose();
      }
    } else {
      // Clear all state even if no changes
      setQuestion('');
      setAnswer('');
      setReferences({});
      setSelectedCitation(null);
      setCitationNumbers([]);
      setIsPreviewMode(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  const assignedCount = Object.keys(references).length;
  const totalCount = citationNumbers.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex h-[90vh] w-[95vw] flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-800">Create Manual Answer</h2>
          <button onClick={handleClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X className="size-6" />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          <div className="flex w-[50%] flex-col border-r border-gray-200 p-6">
            <QuestionInput question={question} onChange={setQuestion} />

            <div className="mt-4 flex flex-1 flex-col">
              <div className="mb-3 flex gap-1 self-start rounded-lg bg-gray-100 p-1">
                <button
                  onClick={() => setIsPreviewMode(false)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    !isPreviewMode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Edit
                </button>
                <button
                  onClick={() => setIsPreviewMode(true)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    isPreviewMode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Preview
                </button>
              </div>

              <div className="flex-1">
                {isPreviewMode ? (
                  <AnswerPreview answer={answer} references={references} caseId={caseId} />
                ) : (
                  <AnswerEditorSimple
                    answer={answer}
                    onChange={setAnswer}
                    onInsertCitation={(marker) => {
                      setAnswer((prev) => prev + marker);
                    }}
                    citationNumbers={citationNumbers}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex w-[50%] flex-col overflow-hidden p-6">
            <div className="mb-4 flex-shrink-0">
              <CitationList
                citationNumbers={citationNumbers}
                references={references}
                selectedCitation={selectedCitation}
                onSelectCitation={setSelectedCitation}
                onRemoveReference={handleRemoveReference}
              />
            </div>

            {selectedCitation && references[selectedCitation] && (currentFact || currentChunk) && (
              <div className="mb-4 flex-shrink-0 rounded-md bg-green-100 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-green-800">Citation [{selectedCitation}]</span>
                  <span className="text-xs font-medium text-green-700">
                    {references[selectedCitation].type === 'fact' ? 'Fact' : 'Chunk'}
                  </span>
                </div>
                <div className="max-h-32 overflow-y-auto rounded bg-white p-2 text-xs text-gray-800">
                  {currentFact ? currentFact.description : currentChunk?.content}
                </div>
              </div>
            )}

            <div className="flex min-h-0 flex-1 flex-col border-t border-gray-200 pt-4">
              {selectedCitation && (
                <CitationAssigner
                  caseId={caseId}
                  selectedCitation={selectedCitation}
                  onAssign={handleAssignReference}
                  currentlyAssignedId={currentRef?.id}
                  currentlyAssignedType={currentRef?.type}
                />
              )}
            </div>
          </div>
        </div>

        <ModalFooter
          onCancel={handleClose}
          onSave={handleSave}
          isLoading={isCreating}
          hasUnassignedCitations={assignedCount < totalCount}
        />
      </div>
    </div>
  );
};

export default ManualAnswerModal;
